// What hovering a node says, on every graph: what the node is (its kind, where it belongs, how it's set up) and its
// insights (the figures LINQ has worked out for it). One card, built here, so the model canvas, the actions graph and
// the lens graphs read the same way
import { formatCurrency, formatNumber } from '@angular/common';
import { AnalysisNode } from '../common/analysis-model';
import { ACTION_FREQUENCY_TYPE, ACTION_TYPE, FREQUENCY_TYPE, NODE_SUPERTYPE, NODE_TYPE, NodeInsights, WORK_TYPE } from '../common/linqset-base';
import { capacityDescription, getDurationDescription, LinqsetNode } from '../common/linqset-model';
import { Currency, getTotalModelCost, ModelDetails, ModelInsights } from '../common/organisation';
import { capacityIsKnown, capacitySource } from '../model-services/role-capacity';

export interface TipFigure {
    label: string;
    value: string;
    note?: string;                      // a word or two after the value, such as "a year"
    highlight?: boolean;                // the figure the graph is coloured by just now
}

export interface NodeTip {
    kind: string;                       // what the node is: "Action", "Role", "Output"...
    title: string;
    where?: string;                     // the model, or the organisation
    description?: string;
    facts: { label: string; value: string }[];      // how it's set up
    figures: TipFigure[];               // its insights
    footer?: string;
}

export interface TipFormat {
    count: (v: number) => string;
    hours: (v: number) => string;
    money: (v: number) => string;
    percent: (share: number) => string;
}

// numbers as the tooltips show them: whole, with thousands separated; money in the organisation's currency
export const tipFormat = (currency: Currency, locale = 'en-NZ'): TipFormat => {
    const whole = (v: number) => formatNumber(Math.round(v || 0), locale, '1.0-0');
    return {
        count: whole,
        hours: (v: number) => (Math.abs(v) < 10 && v !== 0 ? formatNumber(v, locale, '1.0-1') : whole(v)),
        money: (v: number) => formatCurrency(v || 0, locale, currency.symbol, currency.code, '1.0-0'),
        percent: (share: number) => `${Math.round((share || 0) * 100)}%`,
    };
};

const TYPE_NAMES: Partial<Record<NODE_TYPE, string>> = {
    [NODE_TYPE.INFORMATION]: 'Information', [NODE_TYPE.APPLICATION]: 'Application', [NODE_TYPE.SERVICE]: 'Service', [NODE_TYPE.DATABASE]: 'Database',
    [NODE_TYPE.FILE]: 'File', [NODE_TYPE.PAPER]: 'Paper', [NODE_TYPE.VERBAL]: 'Verbal', [NODE_TYPE.ACTION]: 'Action', [NODE_TYPE.CAPTURE]: 'Capture',
    [NODE_TYPE.PROCESS]: 'Process', [NODE_TYPE.OUTCOME]: 'Outcome', [NODE_TYPE.SOMETHING_HAPPENS_HERE]: 'Something happens here', [NODE_TYPE.SYSTEM]: 'System',
    [NODE_TYPE.AI_AGENT]: 'AI agent', [NODE_TYPE.ROLE]: 'Role', [NODE_TYPE.EXTERNAL_ORGANISATION]: 'External organisation', [NODE_TYPE.PERSON]: 'Person',
    [NODE_TYPE.TEAM]: 'Team', [NODE_TYPE.INFRASTRUCTURE]: 'Infrastructure', [NODE_TYPE.ON_PREMISE]: 'On-premise infrastructure',
    [NODE_TYPE.CLOUD_BASED]: 'Cloud infrastructure', [NODE_TYPE.COMMENT]: 'Comment', [NODE_TYPE.TODO]: 'To do', [NODE_TYPE.OUTCOMES]: 'Outcome',
    [NODE_TYPE.ASSET]: 'Information asset', [NODE_TYPE.MODEL]: 'Model',
};

const SUPER_TYPE_NAMES: Partial<Record<NODE_SUPERTYPE, string>> = {
    [NODE_SUPERTYPE.ACTION]: 'Action', [NODE_SUPERTYPE.INFORMATION]: 'Information', [NODE_SUPERTYPE.SYSTEM]: 'System', [NODE_SUPERTYPE.ROLE]: 'Role',
    [NODE_SUPERTYPE.PEOPLE]: 'People', [NODE_SUPERTYPE.INFRASTRUCTURE]: 'Infrastructure', [NODE_SUPERTYPE.OUTCOMES]: 'Outcome',
};

export const nodeTypeName = (type: NODE_TYPE | undefined, superType?: NODE_SUPERTYPE) =>
    (type !== undefined && TYPE_NAMES[type]) || (superType !== undefined && SUPER_TYPE_NAMES[superType]) || 'Node';

export const HOW_DONE: Record<ACTION_TYPE, string> = {
    [ACTION_TYPE.NONE]: 'No role or system assigned',
    [ACTION_TYPE.AUTOMATED]: 'Automated: done by a system',
    [ACTION_TYPE.MANUAL]: 'Done by people',
    [ACTION_TYPE.BOTH]: 'Done by people using systems',
    [ACTION_TYPE.EXTERNAL]: 'Done outside the organisation',
};
const WAITING = 'Items wait here: nobody works on it';

// how long a step takes, each time: in hours or minutes, as reads best
const durationText = (seconds: number) => {
    const unit = (v: number, one: string) => `${formatNumber(v, 'en-NZ', '1.0-1')} ${v === 1 ? one : one + 's'} each`;
    if (seconds >= 3600) return unit(Math.round(seconds / 360) / 10, 'hour');
    if (seconds >= 60) return unit(Math.round(seconds / 6) / 10, 'minute');
    return unit(Math.round(seconds), 'second');
};

// how an action's load is worked out when several inputs feed it
const COMBINES: Partial<Record<ACTION_FREQUENCY_TYPE, string>> = {
    [ACTION_FREQUENCY_TYPE.SUM]: 'Adds up what comes in', [ACTION_FREQUENCY_TYPE.MAXIMUM]: 'Takes the largest input',
    [ACTION_FREQUENCY_TYPE.MINIMUM]: 'Goes with its least input, taking what the others have sent with each run', [ACTION_FREQUENCY_TYPE.AVERAGE]: 'Averages what comes in',
};

const shorten = (text: string | undefined, max = 160) => {
    const t = (text || '').trim();
    return t.length > max ? t.slice(0, max - 1).trimEnd() + '…' : t;
};

const list = (names: string[], max = 4) => {
    const n = names.filter(x => !!x);
    return n.length <= max ? n.join(', ') : `${n.slice(0, max).join(', ')} and ${n.length - max} more`;
};

// how often an action happens, or how much time a role or person gives: as the node table says it
const frequencyFact = (node: LinqsetNode, insights: NodeInsights | undefined, defaultHours = 0): string | undefined => {
    const f = node.properties?.frequency;
    if (node.superType === NODE_SUPERTYPE.ACTION) {
        if (!f || !f.type || f.type === FREQUENCY_TYPE.USE_CALCULATED) return insights?.isInput && !insights?.numberProcessed ? 'Not set: add how often it happens' : 'Worked out from what comes in';
        return `${formatNumber(f.ocurrences || 0, 'en-NZ', '1.0-2')} each ${String(f.type).toLowerCase()}`;
    }
    if (node.superType === NODE_SUPERTYPE.ROLE || node.superType === NODE_SUPERTYPE.PEOPLE) {
        switch (capacitySource(node, insights, defaultHours)) {
            case 'people': return 'From the people in the role';
            case 'set': return capacityDescription(node.properties) || undefined;
            case 'default': return `The organisation's default: ${formatNumber(defaultHours, 'en-NZ', '1.0-0')} hours a year${node.superType === NODE_SUPERTYPE.ROLE ? ' for each person' : ''}`;
            default: return node.type === NODE_TYPE.EXTERNAL_ORGANISATION ? undefined
                : node.superType === NODE_SUPERTYPE.ROLE ? 'Not set: taken to match its work' : 'Not set';
        }
    }
    return undefined;
};

export interface LinqsetTipContext {
    format: TipFormat;
    modelName?: string;                 // the model the node is in, when it isn't the one being looked at
    defaultHours?: number;              // the organisation's full-time year, which roles and people without capacity of their own take
    workspaceName?: string;
    roles?: string[];                   // an action's roles and systems, by name
    systems?: string[];
    tags?: { group: string; tag: string }[];
    highlight?: TipFigure;              // what the canvas is coloured by, if anything
}

// a node on the model canvas: a LINQ node with its insights for the model (or organisation) being looked at
// where work ends or starts (outputs, outcomes and captures): what it takes all the way to get there, and the value and
// revenue that come of it, shown even where they are nothing
// what a step, role or system contributes (its share of the value it helps create, so they add up) and supports (the
// value of everything depending on it), where there is any
// and the revenue it creates less the cost of its work, where it's on the way to revenue: below nothing, the work costs
// more than it creates
// and what would be lost if it stopped, as the items only it handles have no other route
const valueFigures = (f: TipFormat, t: { contributedValue?: number; contributedRevenue?: number; value?: number; revenue?: number; atRiskValue?: number; atRiskRevenue?: number }, cost = 0): TipFigure[] => [
    ...(t.contributedValue ? [{ label: 'Value contribution', value: f.count(t.contributedValue), note: 'a year, its share' }] : []),
    ...(t.contributedRevenue ? [{ label: 'Revenue contribution', value: f.money(t.contributedRevenue), note: 'a year, its share' }] : []),
    ...((t.contributedRevenue || (t.revenue && cost)) ? [marginFigure(f, t.contributedRevenue || 0, cost)] : []),
    ...(t.value ? [{ label: 'Value supported', value: f.count(t.value), note: 'a year, depending on it' }] : []),
    ...(t.revenue ? [{ label: 'Revenue supported', value: f.money(t.revenue), note: 'a year, depending on it' }] : []),
    ...(t.atRiskValue ? [{ label: 'Value at risk', value: f.count(t.atRiskValue), note: 'a year, lost if it stopped' }] : []),
    ...(t.atRiskRevenue ? [{ label: 'Revenue at risk', value: f.money(t.atRiskRevenue), note: 'a year, lost if it stopped' }] : []),
];

export const marginFigure = (f: TipFormat, revenue: number, cost: number): TipFigure => ({
    label: 'Revenue margin',
    value: revenue - cost < 0 ? `−${f.money(cost - revenue)}` : f.money(revenue - cost),
    note: cost > 0 ? (revenue > 0 ? `a year, ${(revenue / cost).toFixed(1)}× its cost` : 'a year, creating no revenue') : 'a year',
});

// an output's or outcome's totals are what it took to get to it; a capture's, where the work starts, are what it takes
// to process the items submitted in it
const totalFigures = (f: TipFormat, t: { cost?: number; hours?: number; carbon?: number; value?: number; revenue?: number }, capture = false): TipFigure[] => {
    const what = capture ? 'to process what is captured' : 'to get here';
    return [
        { label: 'Total cost', value: f.money(t.cost || 0), note: `a year, ${what}` },
        { label: 'Total hours', value: f.hours(t.hours || 0), note: `a year, ${what}` },
        { label: 'Total carbon', value: f.count(t.carbon || 0), note: `kg CO2e a year, ${what}` },
        { label: 'Total value', value: f.count(t.value || 0), note: 'a year' },
        { label: 'Total revenue', value: f.money(t.revenue || 0), note: 'a year' },
    ];
};
const analysisTotals = (f: TipFormat, node: AnalysisNode) =>
    totalFigures(f, { cost: node.totalCost, hours: node.totalHours, carbon: node.totalCarbon, value: node.value, revenue: node.revenue }, node.type === NODE_TYPE.CAPTURE);

export const linqsetNodeTip =(node: LinqsetNode, insights: NodeInsights | undefined, ctx: LinqsetTipContext): NodeTip => {
    const f = ctx.format;
    const facts: { label: string; value: string }[] = [];
    const figures: TipFigure[] = [];
    const i = insights;
    const superType = node.superType;
    const isAction = superType === NODE_SUPERTYPE.ACTION;
    const isInfo = superType === NODE_SUPERTYPE.INFORMATION || superType === NODE_SUPERTYPE.OUTCOMES;
    const isRole = superType === NODE_SUPERTYPE.ROLE || superType === NODE_SUPERTYPE.PEOPLE;
    const isSystem = superType === NODE_SUPERTYPE.SYSTEM || superType === NODE_SUPERTYPE.INFRASTRUCTURE;

    // where items wait, the duration is how long they wait and there are no hours of work (see WORK_TYPE)
    const waits = isAction && i?.workKind === WORK_TYPE.WAITING;
    if (isAction) {
        if (waits) facts.push({ label: 'How', value: WAITING });
        else if (i?.actionType !== undefined) facts.push({ label: 'How', value: HOW_DONE[i.actionType] || HOW_DONE[ACTION_TYPE.NONE] });
        if (ctx.roles?.length) facts.push({ label: 'Roles', value: list(ctx.roles) });
        if (ctx.systems?.length) facts.push({ label: 'Systems', value: list(ctx.systems) });
        const frequency = frequencyFact(node, i);
        if (frequency) facts.push({ label: 'Frequency', value: frequency });
        const duration = getDurationDescription(node.properties?.duration);
        if (duration) facts.push({ label: waits ? 'Wait' : 'Takes', value: duration });
        const combine = COMBINES[node.properties?.actionProcessType as ACTION_FREQUENCY_TYPE];
        if (combine && (!frequency || frequency.startsWith('Worked out'))) facts.push({ label: 'Inputs', value: combine });
        if (ctx.tags?.length) facts.push({ label: 'Tags', value: ctx.tags.map(t => `${t.tag} (${t.group})`).join(', ') });
    }
    if (isInfo) {
        const roleOf = i?.isInput && i?.isOutput ? 'An input and an output of the model' : i?.isInput ? 'An input: where the work starts' : i?.isOutput ? 'An output: where the work ends' : undefined;
        if (roleOf) facts.push({ label: 'Place', value: roleOf });
        if (node.external) facts.push({ label: 'Shared', value: 'Connects to other models' });
        if (node.properties?.outputValue) facts.push({ label: 'Value each', value: f.count(node.properties.outputValue) });
        // outcomes aren't used by anything, so they have no derived value
        if (superType === NODE_SUPERTYPE.INFORMATION && i?.derivedValue) facts.push({ label: 'Derived value', value: `${f.count(i.derivedValue)} of 100, from how it is used` });
        if (node.properties?.outputRevenue) facts.push({ label: 'Revenue each', value: f.money(node.properties.outputRevenue) });
    }
    if (isRole) {
        const frequency = frequencyFact(node, i, ctx.defaultHours);
        if (frequency) facts.push({ label: 'Capacity', value: frequency });
        const people = node.properties?.people || i?.numPeople;
        if (people) facts.push({ label: 'People', value: f.count(people) });
        if (node.properties?.percentageModelled && node.properties.percentageModelled < 100) facts.push({ label: 'Modelled', value: `${node.properties.percentageModelled}% of their time` });
    }
    if (isSystem) {
        if (node.properties?.units && node.properties.units !== 1) facts.push({ label: 'Units', value: f.count(node.properties.units) });
        if (node.properties?.lifetime) facts.push({ label: 'Lifetime', value: `${node.properties.lifetime} years` });
        if (node.properties?.percentageModelled && node.properties.percentageModelled < 100) facts.push({ label: 'Modelled', value: `${node.properties.percentageModelled}% of it` });
    }

    if (i) {
        const cost = i.allocatedCostsByType?.total || 0;
        const carbon = i.allocatedCarbonByScope?.total || 0;
        if (isAction || isInfo) {
            if (waits) figures.push({ label: 'Waiting', value: f.hours(i.waitHours || 0), note: 'hours a year, not work' });
            else figures.push({ label: isInfo ? 'Items' : 'Hours', value: isInfo ? f.count(i.numberProcessed) : f.hours(i.hours), note: 'a year' });
            if (isAction) figures.push({ label: 'Items', value: f.count(i.numberProcessed), note: 'a year' });
        }
        if (isRole) {
            figures.push({ label: 'Work', value: f.hours(i.hours), note: 'hours a year' });
            // a role given just enough capacity for its work would always read 100% used, which says nothing
            if (i.modelledCapacity && capacityIsKnown(node, i, ctx.defaultHours || 0)) figures.push({ label: 'Capacity', value: f.hours(i.modelledCapacity), note: `hours a year · ${f.percent(i.hours / i.modelledCapacity)} used` });
            if (i.numActionsSupported) figures.push({ label: 'Actions', value: f.count(i.numActionsSupported) });
        }
        if (isSystem) {
            if (i.hours) figures.push({ label: 'Used', value: f.hours(i.hours), note: 'hours a year' });
            if (i.numActionsSupported) figures.push({ label: 'Actions', value: f.count(i.numActionsSupported) });
            if (i.numInfoSupplied) figures.push({ label: 'Information supplied', value: f.count(i.numInfoSupplied), note: 'times' });
        }
        if (cost) figures.push({ label: 'Cost', value: f.money(cost), note: 'a year' });
        if (carbon) figures.push({ label: 'Carbon', value: f.count(carbon), note: 'kg CO2e a year' });
        // where work ends or starts (outputs, outcomes and captures): what it takes all the way to get here, or for a
        // capture to process what is captured, and the value and revenue that come of it, even where they are nothing
        const ends = (superType === NODE_SUPERTYPE.INFORMATION && i.isOutput) || superType === NODE_SUPERTYPE.OUTCOMES || node.type === NODE_TYPE.CAPTURE;
        if (ends) {
            figures.push(...totalFigures(f, { cost: i.totalAllocatedCostsByType?.total, hours: i.totalHours, carbon: i.totalCarbon, value: i.value, revenue: i.revenue }, node.type === NODE_TYPE.CAPTURE));
        } else {
            if (isInfo && i.value) figures.push({ label: 'Value', value: f.count(i.value), note: 'a year' });
            if (isInfo && i.revenue) figures.push({ label: 'Revenue', value: f.money(i.revenue), note: 'a year' });
        }
        if ((isRole || isSystem) && i.outputsSupported) figures.push({ label: 'Outputs supported', value: f.count(i.outputsSupported) });
        if (isAction || isRole || isSystem) figures.push(...valueFigures(f, i, i.allocatedCostsByType?.total || 0));
    }
    if (ctx.highlight) figures.unshift({ ...ctx.highlight, highlight: true });

    return {
        kind: nodeTypeName(node.type, node.superType),
        title: node.name || 'Unnamed',
        where: ctx.modelName,
        description: shorten(node.description),
        facts, figures,
        footer: i ? undefined : 'No insights yet: save the model to work them out',
    };
};

export interface ModelTipContext {
    format: TipFormat;
    tags?: string[];                    // the model's tags, by name
    project?: 'new' | 'changed';        // what the project being looked at did to the model
}

// a model on the organisation canvas, or linked to the one being looked at: its most telling insights, as the overview
// and model table give them. Its work and what it costs, how much is automated, how loaded its roles are, what its
// outputs earn against that cost, and the cost of work that comes to nothing
export const modelNodeTip = (model: ModelDetails, insights: ModelInsights | undefined, ctx: ModelTipContext): NodeTip => {
    const f = ctx.format;
    const facts: { label: string; value: string }[] = [];
    const figures: TipFigure[] = [];
    if (model.function) facts.push({ label: 'Function', value: String(model.function) });
    if (ctx.tags?.length) facts.push({ label: 'Tags', value: list(ctx.tags) });
    if (ctx.project) facts.push({ label: 'Project', value: ctx.project === 'new' ? 'New in this project' : 'Changed in this project' });

    const actions = insights?.superTypes?.[NODE_SUPERTYPE.ACTION];
    if (insights && actions) {
        const cost = getTotalModelCost(insights);
        const roleHours = insights.superTypes[NODE_SUPERTYPE.ROLE]?.hours || 0;
        const automated = insights.actionTypes?.[ACTION_TYPE.AUTOMATED]?.number || 0;
        const revenue = insights.outputRevenue || 0;
        figures.push({ label: 'Actions', value: f.count(actions.number) });
        figures.push({ label: 'Hours', value: f.hours(actions.hours), note: 'a year' });
        figures.push({ label: 'Cost', value: f.money(cost), note: 'a year' });
        if (actions.number) figures.push({ label: 'Automated', value: f.percent(automated / actions.number), note: 'of actions' });
        if (insights.modelledCapacity) figures.push({ label: 'Role capacity', value: f.percent(roleHours / insights.modelledCapacity), note: `used · ${f.hours(roleHours)} of ${f.hours(insights.modelledCapacity)} hours` });
        if (insights.allocatedCarbonByScope?.total) figures.push({ label: 'Carbon', value: f.count(insights.allocatedCarbonByScope.total), note: 'kg CO2e a year' });
        if (insights.outputValue) figures.push({ label: 'Value', value: f.count(insights.outputValue), note: 'a year, from its outputs' });
        if (revenue) figures.push({ label: 'Revenue', value: f.money(revenue), note: 'a year, from its outputs' });
        if (revenue || cost) figures.push(marginFigure(f, revenue, cost));
        if (insights.frictionCost) figures.push({ label: 'Friction', value: f.money(insights.frictionCost), note: 'a year, on outputs with no value' });
        if (insights.exceptionCost) figures.push({ label: 'Exception work', value: f.money(insights.exceptionCost), note: `a year, on ${f.count(insights.exceptionActions)} ${insights.exceptionActions === 1 ? 'action' : 'actions'}` });
    }

    return {
        kind: 'Model',
        title: model.name || 'Unnamed',
        description: shorten(model.description),
        facts, figures,
        footer: figures.length ? undefined : 'No insights yet: save the model to work them out',
    };
};

// a group of nodes folded together on the canvas
export const groupNodeTip = (name: string, description: string | undefined, insights: ModelInsights | undefined, format: TipFormat): NodeTip => {
    const figures: TipFigure[] = [];
    const actions = insights?.superTypes?.[NODE_SUPERTYPE.ACTION];
    if (actions?.number) figures.push({ label: 'Actions', value: format.count(actions.number) });
    if (actions?.hours) figures.push({ label: 'Hours', value: format.hours(actions.hours), note: 'a year' });
    const cost = insights ? getTotalModelCost(insights) : 0;
    if (cost) figures.push({ label: 'Cost', value: format.money(cost), note: 'a year' });
    return { kind: 'Group', title: name || 'Group', description: shorten(description), facts: [], figures };
};

export interface AnalysisTipContext {
    format: TipFormat;
    modelName?: string;
    nameOf: (key: string) => string;    // a role's or system's name
    tags?: { group: string; tag: string }[];
    highlight?: TipFigure;
}

// a step on the actions graph: an action with its roles and systems folded in, and the information it ends in
export const analysisNodeTip = (node: AnalysisNode, ctx: AnalysisTipContext): NodeTip => {
    const f = ctx.format;
    const facts: { label: string; value: string }[] = [];
    const figures: TipFigure[] = [];
    const isAction = node.superType === NODE_SUPERTYPE.ACTION;
    if (isAction) {
        const waits = node.workKind === WORK_TYPE.WAITING;
        if (waits) facts.push({ label: 'How', value: WAITING });
        else if (node.actionType !== undefined) facts.push({ label: 'How', value: HOW_DONE[node.actionType] || HOW_DONE[ACTION_TYPE.NONE] });
        const roles = (node.roles || []).map(r => ctx.nameOf(r._key)).filter(n => !!n);
        const systems = (node.systems || []).map(s => ctx.nameOf(s)).filter(n => !!n);
        if (roles.length) facts.push({ label: 'Roles', value: list([...new Set(roles)]) });
        if (systems.length) facts.push({ label: 'Systems', value: list([...new Set(systems)]) });
        if (node.isStarting) facts.push({ label: 'Place', value: node.setsFrequency ? 'Where the work starts: how often is set on it' : 'Where the work starts' });
        if (node.durationSeconds) facts.push({ label: waits ? 'Wait' : 'Takes', value: durationText(node.durationSeconds) });
        if (ctx.tags?.length) facts.push({ label: 'Tags', value: ctx.tags.map(t => `${t.tag} (${t.group})`).join(', ') });
        if (waits) figures.push({ label: 'Waiting', value: f.hours(node.waitHours || 0), note: 'hours a year, not work' });
        else figures.push({ label: 'Hours', value: f.hours(node.hours), note: 'a year' });
        figures.push({ label: 'Items', value: f.count(node.items), note: 'a year' });
        if (node.cost) figures.push({ label: 'Cost', value: f.money(node.cost), note: 'a year' });
        if (node.carbon) figures.push({ label: 'Carbon', value: f.count(node.carbon), note: 'kg CO2e a year' });
        figures.push(...valueFigures(f, node, node.cost || 0));
        // a capture, where the work starts, has the totals outputs and outcomes have
        if (node.type === NODE_TYPE.CAPTURE) figures.push(...analysisTotals(f, node));
        else if (node.totalHours && node.totalHours !== node.hours) figures.push({ label: 'To get here', value: f.hours(node.totalHours), note: `hours a year${node.totalCost ? ` · ${f.money(node.totalCost)}` : ''}` });
    } else {
        facts.push({ label: 'Place', value: node.isStarting ? 'Where the work starts' : 'Where the work ends' });
        figures.push({ label: 'Items', value: f.count(node.items), note: 'a year' });
        figures.push(...analysisTotals(f, node));
    }
    if (ctx.highlight) figures.unshift({ ...ctx.highlight, highlight: true });
    return { kind: nodeTypeName(node.type, node.superType), title: node.name || 'Unnamed', where: ctx.modelName, facts, figures };
};

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string) => {
    const e = document.createElement(tag);
    e.className = className;
    if (text !== undefined) e.textContent = text;
    return e;
};

// a plain tooltip. yFiles sets a string tooltip as markup, so text with names in it goes in an element as text
export const textTip = (text: string): HTMLElement => el('div', '', text);

// the card the tooltip shows; text set as text, so names show as written
export const renderNodeTip = (tip: NodeTip): HTMLElement => {
    const root = el('div', 'linq-tip');
    const head = el('div', 'linq-tip-head');
    head.appendChild(el('span', 'linq-tip-kind', tip.kind + (tip.where ? ` · ${tip.where}` : '')));
    head.appendChild(el('strong', 'linq-tip-title', tip.title));
    root.appendChild(head);
    if (tip.description) root.appendChild(el('p', 'linq-tip-description', tip.description));
    if (tip.facts.length) {
        const dl = el('dl', 'linq-tip-facts');
        tip.facts.forEach(fact => {
            dl.appendChild(el('dt', '', fact.label));
            dl.appendChild(el('dd', '', fact.value));
        });
        root.appendChild(dl);
    }
    if (tip.figures.length) {
        const grid = el('div', 'linq-tip-figures');
        tip.figures.forEach(fig => {
            const cell = el('div', 'linq-tip-figure' + (fig.highlight ? ' highlight' : ''));
            cell.appendChild(el('span', 'linq-tip-label', fig.label));
            const value = el('span', 'linq-tip-value', fig.value);
            cell.appendChild(value);
            if (fig.note) cell.appendChild(el('span', 'linq-tip-note', fig.note));
            grid.appendChild(cell);
        });
        root.appendChild(grid);
    }
    if (tip.footer) root.appendChild(el('p', 'linq-tip-footer', tip.footer));
    return root;
};
